<script setup>
import { computed } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';

defineProps({
  isCollapsed: { type: Boolean, default: false },
  /** When true, opens above the trigger (e.g. sidebar footer). When false, opens below (e.g. top bar). */
  openUpward: { type: Boolean, default: false },
  /** Functionbar uses a boxed trigger style to match action buttons. */
  variant: {
    type: String,
    default: 'default',
  },
});

const emit = defineEmits(['close']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');

const menuItems = computed(() => {
  return [
    {
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center text-left cursor-pointer"
        :class="[
          isCollapsed ? 'justify-center' : 'w-full p-1 rounded-lg hover:bg-n-alpha-1',
          variant === 'functionbar'
            ? 'w-[39px] h-[39px] flex-shrink-0 bg-white mr-4 flex items-center justify-center'
            : [{ 'bg-n-alpha-1': isOpen }],
        ]"
        :title="isCollapsed ? currentUser.available_name : undefined"
        @click="toggle"
      >
        <Avatar
          :size="variant === 'functionbar' ? 39 : 32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          :square="variant === 'functionbar'"
          :rounded-full="variant !== 'functionbar'"
        />
        <div v-if="!isCollapsed" class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody
      :class="[
        'z-50 w-80 ltr:left-0 rtl:right-0',
        openUpward ? 'bottom-12 mb-2' : 'top-full mt-2',
      ]"
    >
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <template v-for="item in menuItems" :key="item.label">
        <DropdownItem v-bind="item" />
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>
